import React from "react";

import type { IgLookupRejectedAccount } from "../../../../../api/post";
import { INSTAGRAM_CAMPAIGN_ROUTE } from "../../../../../features/instagram-campaign/campaign-registers";
import { Alert, AlertBody, AlertTitleRow } from "../../../../ui/alert";
import { joinNames } from "../../PostTags/product-catalog";
import {
  rejectedAccountHandles,
  type LookupFormAccount,
} from "./lookup-rejections";

export type InstagramLookupKind = "audio" | "locations";

const COPY: Record<
  InstagramLookupKind,
  {
    testId: string;
    catalogue: string;
    results: string;
    /** What is unavailable when no account could search. */
    unavailable: string;
    allRejectedAction: string;
  }
> = {
  audio: {
    testId: "ig-audio-rejected",
    catalogue: "audio",
    results: "These tracks",
    unavailable: "Instagram audio",
    allRejectedAction: "before you can pick a track",
  },
  locations: {
    testId: "ig-locations-rejected",
    catalogue: "locations",
    results: "These places",
    unavailable: "Instagram location search",
    allRejectedAction: "before you can search Instagram locations",
  },
};

export interface InstagramLookupNoticeProps {
  kind: InstagramLookupKind;
  rejected: IgLookupRejectedAccount[];
  formAccounts: LookupFormAccount[];
  /**
   * `results`: another account answered. `empty`: it answered with nothing.
   * `failed`: the search failed for another reason. `all`: no account could
   * search.
   */
  outcome: "results" | "empty" | "failed" | "all";
}

/**
 * Names the selected accounts an Instagram lookup skipped, and why. Not a live
 * region itself: the caller keeps one mounted around it, so a notice that appears
 * after a search is announced.
 */
export const InstagramLookupNotice: React.FC<InstagramLookupNoticeProps> = ({
  kind,
  rejected,
  formAccounts,
  outcome,
}) => {
  const copy = COPY[kind];
  const allRejected = outcome === "all";
  const plural = rejected.length > 1;
  const reconnectHandles = rejectedAccountHandles(
    rejected.filter(({ reason }) => reason === "token_rejected"),
    formAccounts
  );
  const noCredentialsHandles = rejectedAccountHandles(
    rejected.filter(({ reason }) => reason === "no_credentials"),
    formAccounts
  );
  const noCredentialsNames = joinNames(noCredentialsHandles);

  const sentences = [
    reconnectHandles.length > 0
      ? `Instagram needs you to reconnect ${joinNames(reconnectHandles)}${
          allRejected ? ` ${copy.allRejectedAction}` : ""
        }.`
      : undefined,
    noCredentialsHandles.length > 0
      ? allRejected && reconnectHandles.length === 0
        ? `${copy.unavailable} is not available for ${noCredentialsNames} from Sked Social yet.`
        : `${noCredentialsNames} cannot search Instagram ${copy.catalogue} from Sked Social yet.`
      : undefined,
    outcome === "results"
      ? `${copy.results} come from another selected account.`
      : undefined,
  ].filter(Boolean);

  return (
    <Alert variant="warning" role="note" data-testid={copy.testId}>
      <AlertTitleRow variant="warning" className="font-inter">
        {allRejected
          ? `${copy.unavailable} is unavailable`
          : `${plural ? "Some accounts" : "One account"} could not search ${copy.catalogue}`}
      </AlertTitleRow>
      <AlertBody className="pl-7">
        <p className="m-0 font-inter text-xs leading-5 text-neutral-700">
          {sentences.join(" ")}
        </p>
        {reconnectHandles.length > 0 ? (
          <a
            href={INSTAGRAM_CAMPAIGN_ROUTE}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-fit items-center gap-1 font-inter text-xs font-medium text-blue-500 no-underline hover:underline"
          >
            Reconnect{" "}
            {reconnectHandles.length > 1 ? "accounts" : reconnectHandles[0]}{" "}
            <span className="sr-only">(opens in a new tab)</span>
            <i
              className="fa-regular fa-arrow-up-right-from-square text-[10px]"
              aria-hidden="true"
            />
          </a>
        ) : null}
      </AlertBody>
    </Alert>
  );
};
